/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSignalReady } from "contexts/globalLoadingScreen";
import { FirebaseError } from "firebase/app";
import {
  AuthProvider,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onIdTokenChanged,
  signInWithPopup,
  User,
} from "firebase/auth";
import { useEffect, useState } from "react";
import { useSignInWithEmailAndPassword } from "react-firebase-hooks/auth";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { backendApi } from "src/store/api/enhancedApi";

const enum LoginProvider {
  GOOGLE,
  MICROSOFT,
}
interface LocationState {
  from: {
    pathname: string;
  };
}

export const SignInPage = () => {
  useSignalReady(); // hide loading screen

  const auth = getAuth();
  const googleAuthProvider = new GoogleAuthProvider();
  const microsoftAuthProvider = new OAuthProvider("microsoft.com");
  microsoftAuthProvider.setCustomParameters({
    tenant: "common",
  });
  const authProviders: Record<LoginProvider, AuthProvider> = {
    [LoginProvider.GOOGLE]: googleAuthProvider,
    [LoginProvider.MICROSOFT]: microsoftAuthProvider,
  };
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [signInWithEmailAndPassword, , loading, error] =
    useSignInWithEmailAndPassword(auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [popupProvider, setPopupProvider] = useState<LoginProvider | null>(
    null
  );
  const isSigningIn = popupProvider !== null || loading;
  const [conflictEmail, setConflictEmail] = useState<string | null>(null);
  const [linkSent, setLinkSent] = useState(false);
  const [requestSignInLink, { isLoading: sendingLink }] =
    backendApi.endpoints.requestSignInLink.useMutation();

  useEffect(() => {
    let isSubscribed = true;
    const continueAsUser = async (user: User) => {
      try {
        // Pick up any permissions changes the API may have performed.
        await user.getIdTokenResult(true);
      } catch (error) {
        console.error(error);
      }
      // Firebase signs the user out when a refresh fails with
      // auth/user-disabled or auth/user-token-expired.
      if (!isSubscribed || auth.currentUser !== user) return;
      const locationState = (location.state ?? { from: "/" }) as LocationState;
      await navigate(locationState.from);
    };

    // A forced refresh fires the listener again with the new token.
    let isContinuing = false;
    const unsubscribe = onIdTokenChanged(auth, (user) => {
      if (user === null) {
        isContinuing = false;
        return;
      }
      if (isContinuing) return;
      isContinuing = true;
      void continueAsUser(user);
    });
    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, [auth, location.state, navigate]);

  const handleSignIn = (login: LoginProvider) => {
    setPopupProvider(login);
    signInWithPopup(auth, authProviders[login]).catch((error: unknown) => {
      setPopupProvider(null);
      handleError(error);
    });

    // Resetting the whole API state here is probably overkill, but is a good
    // way to guarantee that no information is carried between users until we
    // can work out how to invalidate only the current user query
    dispatch(backendApi.util.resetApiState());
  };

  const handleClick = () => {
    if (popupProvider !== null) return;
    signInWithEmailAndPassword(email, password).catch((error: unknown) => {
      if (error instanceof FirebaseError) {
        if (error.message === "auth/invalid-email") {
          setErrorMessage(() => t("invalidEmail"));
        } else if (error.message === "auth/wrong-password") {
          setErrorMessage(() => t("invalidPassword"));
        }
      }
    });
  };

  const getTenantFromLocation = (): string | undefined => {
    const locationState = location.state as LocationState | null;
    const pathSegments = locationState?.from?.pathname?.split("/");
    return pathSegments?.find((s) => s.length > 0);
  };

  const handleError = (error: unknown) => {
    if (
      error instanceof FirebaseError &&
      error.code === "auth/account-exists-with-different-credential"
    ) {
      const emailFromError =
        (error.customData?.email as string | undefined) ?? null;
      if (emailFromError) {
        setConflictEmail(emailFromError);
        localStorage.setItem("emailForSignIn", emailFromError);
        const tenant = getTenantFromLocation();
        if (tenant) {
          localStorage.setItem("tenantForSignIn", tenant);
        }
      }
    }
  };

  const handleSendSignInLink = async () => {
    if (!conflictEmail) return;
    const tenant =
      getTenantFromLocation() ?? localStorage.getItem("tenantForSignIn");
    if (!tenant) {
      setErrorMessage(t("signInLinkSendFailed"));
      return;
    }
    try {
      await requestSignInLink({
        tenantName: tenant,
        requestSignInLinkRequest: { email: conflictEmail },
      }).unwrap();
      setLinkSent(true);
    } catch {
      setErrorMessage(t("signInLinkSendFailed"));
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        height: "100%",
        px: { xs: 2, sm: 0 },
        display: "flex",
        alignItems: "center",
      }}
    >
      <Paper sx={{ width: "100%" }} elevation={2}>
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="stretch"
          useFlexGap
          sx={{ px: { xs: 3, sm: 8 }, pt: 4, pb: 4 }}
        >
          <Typography variant="h2" sx={{ mt: 2, margin: "auto" }}>
            {t("scrapChef")}
          </Typography>
          {conflictEmail ? (
            linkSent ? (
              <Alert sx={{ mt: 3 }} severity="success">
                {t("signInLinkSent")}
              </Alert>
            ) : (
              <>
                <Alert sx={{ mt: 3 }} severity="info">
                  {t("signInConflictMessage")}
                </Alert>
                <LoadingButton
                  loading={sendingLink}
                  variant="outlined"
                  sx={{ mt: 1, textTransform: "none", borderRadius: 0 }}
                  onClick={() => void handleSendSignInLink()}
                >
                  {t("sendSignInLink")}
                </LoadingButton>
                {errorMessage ? (
                  <Alert sx={{ mt: 1 }} severity="error">
                    {errorMessage}
                  </Alert>
                ) : null}
              </>
            )
          ) : (
            <>
              <LoadingButton
                variant="outlined"
                loading={popupProvider === LoginProvider.GOOGLE}
                disabled={isSigningIn}
                onClick={() => {
                  handleSignIn(LoginProvider.GOOGLE);
                }}
                startIcon={<GoogleIcon />}
                sx={{ mt: 3, textTransform: "none", borderRadius: 0 }}
              >
                {t("signInWithGoogle")}
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                loading={popupProvider === LoginProvider.MICROSOFT}
                disabled={isSigningIn}
                onClick={() => {
                  handleSignIn(LoginProvider.MICROSOFT);
                }}
                sx={{ mt: 1, textTransform: "none", borderRadius: 0 }}
                startIcon={<MicrosoftIcon />}
              >
                {t("signInWithMicrosoft")}
              </LoadingButton>
              <Divider sx={{ m: 1 }}>
                <Typography>{t("loginOr")}</Typography>
              </Divider>
              <TextField
                label={t("loginEmail")}
                type="email"
                fullWidth
                variant="outlined"
                sx={{ mt: 1 }}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    handleClick();
                  }
                }}
              />
              <TextField
                label={t("loginPassword")}
                type="password"
                fullWidth
                variant="outlined"
                autoComplete="current-password"
                sx={{ mt: 1, textTransform: "none", borderRadius: 0 }}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    event.stopPropagation();
                    handleClick();
                  }
                }}
              />
              <LoadingButton
                loading={loading}
                disabled={popupProvider !== null}
                type="submit"
                variant="outlined"
                sx={{ mt: 2, textTransform: "none", borderRadius: 0 }}
                onClick={handleClick}
              >
                {t("logIn")}
              </LoadingButton>
              {error ? (
                <Alert sx={{ mt: 1 }} severity="error">
                  {t("loginFailed")} {errorMessage}
                </Alert>
              ) : null}
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};
